import React, { useMemo, Fragment, useCallback } from 'react';
import TableCommon from 'components/TableComponent/TableCommon';
import useFilters from 'hooks/useFilters';
import useGetListTransaction from 'hooks/invoices/useGetListTransaction';
import {
  PayViaEnum,
  // optionsTypeDownloadInvoice,
  optionsStatusInvoice,
  SIZE_ICON,
  optionsReferralStatusInvoice,
  tabOfSettings,
} from 'consts/data';
import NewDesign from 'components/NewDesign';
import FilterContainer from 'components/FilterContainer';
import { FastField, Form, Formik } from 'formik';
import SelectField from 'customField/SelectField';
import FromToPickerField from 'customField/FromToPickerField';
import { CommonIcons } from 'components/Icons';
import { getQueryStringFromURL, getRowInTable, insert } from 'helpers';
import { genStatusInvoiceColor } from 'consts/colorChart';
import { isArray } from 'lodash';
import CellActionTransaction from './Cells/CellActionTransaction';
import getUser from 'redux/selector/getUser';
import { Redirect } from 'react-router-dom';
import { AppRoutes } from 'consts/routesPath';
import { useTimeHelpers } from 'providers/TimezoneProvider';

const headerSwitcher = (type) => {
  const temp = {
    [PayViaEnum.BankTransfer]: 'Released / Amount',
    [PayViaEnum.CreditCard]: 'Amount',
    [PayViaEnum.Referral]: 'Commission',
  };

  return temp?.[type] || '';
};

const typeInvoiceInitial = getQueryStringFromURL()?.type || PayViaEnum.BankTransfer;

//! Main component
const Transaction = () => {
  //! State
  const {
    settings: { enableCreditCard },
    payMethodsAvailable,
  } = getUser();
  const { showDateTimeByTimezone } = useTimeHelpers();

  if (!enableCreditCard && typeInvoiceInitial === PayViaEnum.CreditCard) {
    return <Redirect to={`${AppRoutes.Settings}?tab=${tabOfSettings.profile}`} />;
  }

  const initialFilters = useMemo(() => {
    return {
      type: typeInvoiceInitial,
      start: '',
      end: '',

      offset: 0,
      limit: 10,

      //* Bank type
      statuses: [],
      showDeletedFile: false,

      //* Credit type
      afterId: '',

      dateRanges: {
        from: null,
        to: null,
      },
      invoiceFormat: 'pdf',
    };
  }, [typeInvoiceInitial]);

  const { filters, setFilters, handleSearch, resetFilterToInitial, increaseOffset, putValueToURL } =
    useFilters(initialFilters);
  const { data, loading, loadingMore, hasMore, refetch, refetching } =
    useGetListTransaction(filters);

  const dataInvoices = data?.result || [];
  const typeInvoices = data?.type || '';

  const afterId =
    isArray(dataInvoices) && dataInvoices.length > 1
      ? dataInvoices[dataInvoices.length - 1]?.id
      : '';

  //! Function
  const fetchMore = useCallback(() => {
    if (typeInvoices === PayViaEnum.CreditCard) {
      if (afterId) {
        setFilters((prevFilters) => {
          return {
            ...prevFilters,
            afterId,
          };
        });
      }
      return;
    }

    increaseOffset();
  }, [increaseOffset, typeInvoices, afterId, handleSearch]);

  // const downloadInvoice = useCallback(
  //   (typeInvoice, { id, type }) =>
  //     () => {
  //       const hostname = isLocalhost ? 'http://34.102.207.102' : '';
  //       const link = isLocalhost
  //         ? '/tb/api/v2/authenticated/user/invoice/download'
  //         : DOWNLOAD_INVOICE_URL;

  //       const url = `${hostname}${link}?id=${id}&type=${type}&invoiceType=${typeInvoice}&sessionId=${jwt}`;

  //       window.open(
  //         url,
  //         '_blank',
  //         `titlebar=no,location=no,height=1000,width=1366,scrollbars=yes,status=yes`
  //       );
  //     },
  //   [jwt]
  // );

  const columns = useMemo(() => {
    let result = [
      {
        Header: 'ID',
        accessor: 'id',
      },
      {
        Header: headerSwitcher(typeInvoices),
        accessor: 'amount',
        Cell: (props) => {
          const { amount, released, amountInCent } = getRowInTable(props);

          if (typeInvoices === PayViaEnum.CreditCard) {
            return <NewDesign.NumberFormat value={amountInCent ? amountInCent / 100 : 0} />;
          }

          if (typeInvoices === PayViaEnum.Referral) {
            return <NewDesign.NumberFormat value={amount || 0} />;
          }

          return (
            <Fragment>
              <NewDesign.NumberFormat value={released || 0} />
              <NewDesign.Typography> / </NewDesign.Typography>
              <NewDesign.NumberFormat value={amount || 0} />
            </Fragment>
          );
        },
      },
      {
        Header: 'Currency',
        accessor: 'currency',
        Cell: (props) => {
          const { currency } = getRowInTable(props);

          if (currency) {
            return currency;
          }

          return '$';
        },
      },
      {
        Header: 'Type',
        accessor: 'type',
        Cell: (cellProps) => {
          return typeInvoices;
        },
      },
      {
        Header: 'Created date',
        accessor: 'created',
        Cell: (cellProps) => {
          const { created } = getRowInTable(cellProps);

          if (created) {
            return showDateTimeByTimezone(created);
          }

          return '-';
        },
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: (cellProps) => {
          const { status } = getRowInTable(cellProps);

          return <NewDesign.Badge color={genStatusInvoiceColor(status)}>{status}</NewDesign.Badge>;
        },
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: (cellProps) => {
          return (
            <CellActionTransaction
              typeInvoices={typeInvoices}
              transaction={getRowInTable(cellProps)}
            />
          );
        },
      },
    ];

    if (typeInvoices === PayViaEnum.CreditCard) {
      result = insert(result, 3, {
        Header: 'Description',
        accessor: 'description',
        Cell: (cellProps) => {
          const { description, failureMessage } = getRowInTable(cellProps);

          return (
            <NewDesign.Typography>
              {description || '-'}
              {failureMessage && (
                <Fragment>
                  <br />
                  <NewDesign.Typography className='color-danger-v2'>
                    {failureMessage || ''}
                  </NewDesign.Typography>
                </Fragment>
              )}
            </NewDesign.Typography>
          );
        },
      });
    }

    return result;
  }, [typeInvoices, showDateTimeByTimezone]);

  return (
    <div className='component:Transactions checkout-form__transaction'>
      <NewDesign.Typography component='h4' className='mb-2'>
        Transactions
      </NewDesign.Typography>

      <Formik
        initialValues={filters}
        enableReinitialize
        onSubmit={(valuesFilters) => {
          handleSearch(valuesFilters);
          putValueToURL({ type: valuesFilters.type });
        }}
      >
        {(helpersFormik) => {
          const isBankTransfer = helpersFormik.values.type === PayViaEnum.BankTransfer;
          const isReferral = helpersFormik.values.type === PayViaEnum.Referral;

          return (
            <Form className='mb-2'>
              <FilterContainer
                renderFields={
                  <div className='invoice-filters'>
                    <div className='invoice-filters__left'>
                      <div style={{ minWidth: 200 }}>
                        <FastField
                          component={SelectField}
                          name='type'
                          label='Type'
                          options={payMethodsAvailable}
                          afterOnChange={() => {
                            helpersFormik.setFieldValue('statuses', []);
                          }}
                        />
                      </div>

                      <div>
                        <FastField
                          component={FromToPickerField}
                          name='dateRanges'
                          label='Created date (From - To)'
                          isClearable
                          hideCustomClear
                        />
                      </div>
                    </div>

                    <div className='invoice-filters__right'>
                      {/* <div>
                        <div style={{ minWidth: 200 }}>
                          <FastField
                            component={SelectField}
                            name='invoiceFormat'
                            label='Invoice format (xml / pdf)'
                            options={optionsTypeDownloadInvoice}
                          />
                        </div>

                        <NewDesign.Button outline>
                          <CommonIcons.Download /> Download
                        </NewDesign.Button>
                      </div> */}
                    </div>

                    {isBankTransfer && (
                      <div className='invoice-filters__bottom'>
                        <div className='statuses'>
                          <FastField
                            isMulti
                            component={SelectField}
                            name='statuses'
                            label='Statuses'
                            placeholder='Select status(es)'
                            options={optionsStatusInvoice}
                            closeMenuOnSelect={false}
                          />
                        </div>
                      </div>
                    )}

                    {isReferral && (
                      <div className='invoice-filters__bottom'>
                        <div className='statuses'>
                          <FastField
                            isMulti
                            component={SelectField}
                            name='statuses'
                            label='Statuses'
                            placeholder='Select status(es)'
                            options={optionsReferralStatusInvoice}
                            closeMenuOnSelect={false}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                }
                renderButtons={
                  <Fragment>
                    <NewDesign.Button
                      variant='text'
                      isLoading={refetching}
                      onClick={() => refetch({ offset: 0 })}
                    >
                      <CommonIcons.Refresh size={SIZE_ICON} /> Refresh
                    </NewDesign.Button>

                    <NewDesign.Button onClick={helpersFormik.handleSubmit}>
                      <CommonIcons.Search size={SIZE_ICON} /> Search
                    </NewDesign.Button>

                    <NewDesign.Button
                      outline
                      onClick={() => {
                        const typeDefault = { type: PayViaEnum.BankTransfer };
                        putValueToURL(typeDefault);
                        resetFilterToInitial(typeDefault);
                        helpersFormik.resetForm();
                      }}
                    >
                      <CommonIcons.Refresh size={SIZE_ICON} /> Reset
                    </NewDesign.Button>
                  </Fragment>
                }
              />
            </Form>
          );
        }}
      </Formik>

      <div style={{ paddingTop: '0.5rem' }}>
        <TableCommon
          columns={columns}
          data={dataInvoices}
          isLoading={loading}
          disablePagination
          disableSortBy
          disabledSelectRow
          width={0}
          isMaterial
          fixedColumn
          //* Loadmore
          isInfiniteLoader
          hasMore={hasMore}
          fetchMore={fetchMore}
          isLoadingMore={loadingMore}
        />
      </div>
    </div>
  );
};

Transaction.defaultProps = {
  data: [],
};

export default React.memo(Transaction);
